import { useState, useCallback, useEffect, memo } from 'react';
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api';
import { useTranslation } from 'react-i18next';

const GOOGLE_MAPS_API_KEY = "AIzaSyBOtiAoF6VeilnU6N6GXGyURinLs0pdLG4";
const libraries = ['places'];

const mapContainerStyle = {
    width: '100%',
    height: '400px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0'
};

const egyptCenter = { lat: 26.8206, lng: 30.8025 };
const egyptBounds = {
    north: 31.8349,
    south: 21.7256,
    west: 24.6981,
    east: 36.8947
};

const parseGoogleMapsLink = (link) => {
    if (!link) return null;
    try {
        // Format: ?q=lat,lng
        const qMatch = link.match(/[?&]q=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
        if (qMatch) return { lat: parseFloat(qMatch[1]), lng: parseFloat(qMatch[2]) };

        // Format: /@lat,lng,zoom
        const atMatch = link.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);
        if (atMatch) return { lat: parseFloat(atMatch[1]), lng: parseFloat(atMatch[2]) };

        return null;
    } catch {
        return null;
    }
};

const GeoLocPicker = ({ setLink, initialLocation }) => {
    const { t } = useTranslation();

    const [selectedLocation, setSelectedLocation] = useState(() =>
        parseGoogleMapsLink(initialLocation)
    );
    const [mapCenter, setMapCenter] = useState(() =>
        parseGoogleMapsLink(initialLocation) ?? egyptCenter
    );

    useEffect(() => {
        if (initialLocation) {
            const parsed = parseGoogleMapsLink(initialLocation);
            if (parsed) {
                setSelectedLocation(parsed);
                setMapCenter(parsed);
            }
            return;
        }

        // No initialLocation — try browser geolocation
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    const userLocation = { lat: latitude, lng: longitude };
                    setSelectedLocation(userLocation);
                    setMapCenter(userLocation);
                    setLink(`https://www.google.com/maps?q=${latitude},${longitude}`);
                },
                (error) => {
                    console.warn('Geolocation error:', error);
                    setMapCenter(egyptCenter);
                }
            );
        } else {
            console.warn('Geolocation not supported by this browser.');
        }
    }, [initialLocation]);

    const handleMapClick = useCallback(
        (event) => {
            const clickedLocation = {
                lat: event.latLng.lat(),
                lng: event.latLng.lng()
            };
            setSelectedLocation(clickedLocation);
            setMapCenter(clickedLocation);
            setLink(`https://www.google.com/maps?q=${clickedLocation.lat},${clickedLocation.lng}`);
        },
        [setLink]
    );

    return (
        <div className="p-4 col-sm-12">
            <h5 className="text-sm font-bold mb-4">
                {t('select-location-on-map')}
            </h5>
            <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY} libraries={libraries}>
                <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    center={mapCenter}
                    zoom={selectedLocation ? 12 : 6}
                    onClick={handleMapClick}
                    options={{
                        restriction: {
                            latLngBounds: egyptBounds,
                            strictBounds: true
                        },
                        streetViewControl: false,
                        mapTypeControl: false
                    }}
                >
                    {selectedLocation && <Marker position={selectedLocation} />}
                </GoogleMap>
            </LoadScript>
        </div>
    );
};

export default memo(GeoLocPicker);